import type {
  TeaserBaseProps,
  TeaserBodyDecoratorsProps,
  TeaserBylineProps,
  TeaserFooterProps,
  TeaserHighlightProps,
  TeaserItalicHeadlineProps,
  TeaserMediaProps,
  TeaserTextProps,
  TeaserThemeProps,
  TeaserVariantProps,
  TeaserVignetteProps,
} from '@bonniernews/dn-design-system-web/assets/types/teaser-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'
import { Body, Content, Media, Title, Vignette } from '@bonniernews/dn-design-system-web/helpers/teaser.tsx'

import { TeaserCard } from '@bonniernews/dn-design-system-web/components/teaser-card/teaser-card.tsx'
import { TeaserFooter } from '@bonniernews/dn-design-system-web/components/teaser-footer/teaser-footer.tsx'

export interface TeaserStandardProps
  extends TeaserBaseProps,
    TeaserThemeProps,
    TeaserHighlightProps,
    TeaserItalicHeadlineProps,
    TeaserVignetteProps,
    TeaserVariantProps,
    TeaserMediaProps,
    TeaserBylineProps,
    TeaserTextProps,
    TeaserBodyDecoratorsProps,
    TeaserFooterProps {
  /** If the teaser should be compact */
  isCompact?: boolean
  variant?: 'default' | 'podcast'
  /** Flag so we can handle square teaser images */
  isSquareImage?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/teaser-standard](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/teaser-standard)
 * - Storybook: [TeaserStandard](https://designsystem.dn.se/?path=/docs/section-teaserstandard--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/teaser-standard/teaser-standard.scss'`
 *
 *
 * ## Javascript
 *
 * This version is rarely used to render a teaser dynamically in the browser. For example, it is used to render reserv-teasers in failover. This version **ONLY** supports the `title`, `text`, `targetLink`, `classNames` and `attributes` parameters.
 *
 * ```javascript
 *  import { dsTeaserStandard } from "@bonniernews/dn-design-system-web/components/teaser-standard/teaser-standard";
 *
 * // NB: javascript only supports a subset of all parameters
 *
 * const parameters = {
 *   title: "Upp på börsen",
 *   text: "Det ser ganska normalt ut på Stockholmsbörsen",
 *   targetLink: "https://www.dn.se/ekonomi/",
 *   attributes: { "data-test" : "test-value" }
 * }
 *
 * const teaserStandardHtml = dsTeaserStandard(parameters);
 * ```
 */
export const TeaserStandard = ({
  title,
  highlight,
  isItalicHeadline,
  targetLink,
  areaType,
  theme,
  variant,
  isCompact,
  duration,
  media,
  isSquareImage = false,
  bylinePicture,
  vignette,
  publicationTime,
  text,
  sticker,
  isFlashingDot = false,
  sectionName,
  classNames,
  attributes = {},
}: TeaserStandardProps) => {
  const componentClassName = 'ds-teaser'

  const classes = formatClassString([
    'ds-teaser--standard',
    variant && `${componentClassName}--${variant}`,
    isCompact && `${componentClassName}--compact`,
    isItalicHeadline && bylinePicture
      ? `${componentClassName}--standard-with-byline`
      : `${componentClassName}--standard-without-byline`,
    vignette && `${componentClassName}--standard-with-vignette`,
    classNames,
  ])

  return (
    <TeaserCard areaType={areaType} targetLink={targetLink} theme={theme} classNames={classes} attributes={attributes}>
      <Content bylinePicture={bylinePicture}>
        {!bylinePicture && <Media media={media} isSquareImage={isSquareImage} rounded={!isItalicHeadline} />}
        <Vignette vignette={vignette} variant={variant} />
        <Title title={title} highlight={highlight} isItalicHeadline={isItalicHeadline} />
        {areaType !== 'right' && <Body text={text} sticker={sticker} isFlashingDot={isFlashingDot} />}
        {variant === 'podcast' && (
          <TeaserFooter
            footerVariant='podcast'
            publicationTime={publicationTime}
            sectionName={sectionName}
            duration={duration}
          />
        )}
        {variant !== 'podcast' && publicationTime && (
          <TeaserFooter footerVariant='informationText' publicationTime={publicationTime} sectionName={sectionName} />
        )}
      </Content>
    </TeaserCard>
  )
}
